"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

/* Home hero background slideshow.
 *
 * This is the ONLY client component on the home page — it renders just the
 * background images. The <h1>, body copy, and CTAs stay in the Server
 * Component (src/app/page.tsx), so the text crawlers index is still plain
 * static HTML (see the SEO notes in CLAUDE.md).
 *
 * With JavaScript disabled the slides sit side by side inside an
 * overflow-hidden viewport, so the first image simply shows as a static hero.
 *
 * Slides translate rather than cross-fade so off-screen images stay outside
 * the viewport and keep their lazy loading — only the first image is
 * preloaded, which protects the LCP.
 */

export type HeroSlide = { src: string; alt?: string };

export default function HeroSlideshow({ slides }: { slides: readonly HeroSlide[] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 45 }, [
    Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: false }),
  ]);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();

    // Reduced-motion visitors still get the rotation (otherwise the hero looks
    // broken on machines with Windows animations switched off) — but the
    // slides cut instead of sliding, so there is no movement on screen.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      emblaApi.reInit({ duration: 0 });
    }
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  return (
    <>
      <div className="absolute inset-0 overflow-hidden" ref={emblaRef} aria-hidden="true">
        <div className="flex h-full">
          {slides.map((s, i) => (
            <div key={s.src} className="relative h-full min-w-0 flex-[0_0_100%]">
              <Image
                src={s.src}
                alt=""
                fill
                className="object-cover"
                sizes="100vw"
                // Slide 1 is the LCP image: preloaded at high priority. The
                // rest load eagerly but at LOW priority — lazy loading would
                // start the fetch only as the slide scrolls in, which shows a
                // blank frame mid-transition.
                {...(i === 0
                  ? { priority: true }
                  : { loading: "eager" as const, fetchPriority: "low" as const })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Slide dots — bottom-right so they clear the headline and CTAs. */}
      <div className="absolute bottom-6 right-5 z-20 flex gap-2 md:bottom-8 md:right-8">
        {slides.map((s, i) => (
          <button
            key={s.src}
            type="button"
            onClick={() => scrollTo(i)}
            aria-label={`Show slide ${i + 1} of ${slides.length}`}
            aria-current={i === selected}
            className={`h-1.5 rounded-full transition-all ${
              i === selected ? "w-6 bg-gold" : "w-1.5 bg-white/45 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </>
  );
}
